import React from 'react';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { Button, message, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PlugPag, Store, StoreGroup } from '@inhouse-market/sdk';
import { cnpjFormatted, cpfFormatted } from '@/helpers/utils';
import Recaptcha from '@/components/Recaptcha';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { SorterResult } from 'antd/lib/table/interface';
import {
    ParamsStoreManagement,
    StoreListOrderBy,
    StoreListOrderDir,
} from '@/store/Store/types';
import { CellWithTooltip } from '../../components/DataView/EditableTable';
import { convertCurrency } from '../../helpers/convertCurrency';
import ConfirmModal from '../../components/Layout/ConfirmModal';
import { ModalContainer } from './style';
import { useAuth } from '../../contexts/Auth';
import { BadgeTypeBilling } from '../Users/ShopManagerV2/BillingData/styled';
import { BillingLabel } from '../Users/ShopManagerV2/BillingData/components/BillingForm/const';

const SPLIT_TYPE_LABELS: Record<string, string> = {
    [PlugPag.EnumSplitType.terminal]: 'Terminal',
    [PlugPag.EnumSplitType.post]: 'Pós',
};

const TableStoreMAnagement = ({
    loading,
    dataSource,
    rowSelection,
    setFilters,
}: {
    loading: boolean;
    dataSource: Store.StoreManagement[];
    rowSelection: any;
    setFilters: React.Dispatch<React.SetStateAction<ParamsStoreManagement>>;
}) => {
    const [open, setOpen] = React.useState(false);
    const [assumeLoading, setAssumeLoading] = React.useState(false);
    const { getAssumedToken } = useAuth();
    const { recaptchaRef, getRecaptchaToken, cancelRecaptcha } = useRecaptcha();
    const navigate = useNavigate();

    const [shoppManagerAssume, setShopManagerAssume] = React.useState<{
        user_id: number;
        shopmanager_id: number;
        email: string;
        isBlocked: number;
        redirect: string;
    }>({
        user_id: 0,
        shopmanager_id: 0,
        email: '',
        isBlocked: 0,
        redirect: '/',
    });

    const handleAssume = async (
        user_id: number,
        shopmanager_id: number,
        email: string,
        isBlocked: number,
        redirect: string,
    ) => {
        setShopManagerAssume({
            user_id,
            shopmanager_id,
            email,
            isBlocked,
            redirect,
        });

        if (!open) {
            setOpen(true);
            return;
        }

        const token = await getRecaptchaToken();
        if (!token) return;

        setAssumeLoading(true);
        try {
            const assumed = await getAssumedToken(user_id, token);
            if (!assumed) return;

            setOpen(false);
            message.info(`Visão de ${email} assumida`);

            if (isBlocked) {
                message.warning(
                    `Este lojista está bloqueado, ele não consegue logar`,
                );
            }

            if (redirect) navigate(redirect);
        } finally {
            setAssumeLoading(false);
        }
    };

    const storesColumns: ColumnsType<Store.StoreManagement> = [
        {
            title: 'ID Loja',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (store: Store.StoreManagement) => (
                <Button
                    type="link"
                    onClick={() =>
                        handleAssume(
                            store?.shopManager?.user?.id, // user_id
                            store?.shopManager?.id, // shopmanager_id
                            store?.shopManager?.user?.email, // email
                            store.shopManager.is_blocked, // is_blocked
                            `/stores/details/data/${store.id}`, // redirect screen
                        )
                    }
                >
                    {store.id}
                </Button>
            ),
        },
        {
            title: 'Loja',
            width: 287,
            ellipsis: true,
            render: (store: Store.StoreManagement) => (
                <Button
                    type="link"
                    onClick={() =>
                        handleAssume(
                            store?.shopManager?.user?.id, // user_id
                            store?.shopManager?.id, // shopmanager_id
                            store?.shopManager?.user?.email, // email
                            store.shopManager.is_blocked, // is_blocked
                            `/stores/details/data/${store.id}`, // redirect screen
                        )
                    }
                >
                    {store.name}
                </Button>
            ),
        },
        {
            title: 'Cidade',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (store: Store.StoreManagement) => store.address.city,
        },
        {
            title: 'Estado',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (store: Store.StoreManagement) => store.address.state,
        },
        {
            title: 'ID Lojista',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (store: Store.StoreManagement) => (
                <Button
                    type="link"
                    onClick={() =>
                        handleAssume(
                            store?.shopManager?.user?.id, // user_id
                            store?.shopManager?.id, // shopmanager_id
                            store?.shopManager?.user?.email, // email
                            store.shopManager.is_blocked, // is_blocked
                            '/', // redirect screen
                        )
                    }
                >
                    {store?.shopManager?.id}
                </Button>
            ),
        },
        {
            title: 'Email Lojista',
            width: 310,
            ellipsis: true,
            render: (store: Store.StoreManagement) => (
                <Button
                    type="link"
                    onClick={() =>
                        handleAssume(
                            store?.shopManager?.user?.id, // user_id
                            store?.shopManager?.id, // shopmanager_id
                            store?.shopManager?.user?.email, // email
                            store.shopManager.is_blocked, // is_blocked
                            '/', // redirect screen
                        )
                    }
                >
                    {store?.shopManager?.user?.email?.toLocaleLowerCase()}
                </Button>
            ),
        },
        {
            title: 'CNPJ',
            width: 310,
            ellipsis: true,
            render: (store: Store.StoreManagement) => {
                const { doc } = store.shopManager;

                if (!doc) return '';

                if (doc.length > 11) {
                    return cnpjFormatted(doc);
                }

                return cpfFormatted(doc);
            },
        },
        {
            title: 'EMAIL PAGBANK',
            width: 310,
            ellipsis: true,
            render: (store: Store.StoreManagement) =>
                store.shopManager.plugpag_email || '',
        },
        {
            title: 'Tipo de Split PagBank',
            dataIndex: 'shopManager',
            width: 148,
            align: 'center',
            render: (shopManager: ShopManager) =>
                SPLIT_TYPE_LABELS[shopManager.pagbank_split_type ?? ''] || '-',
        },
        {
            title: 'Lojista Bloqueado',
            dataIndex: 'shopManager',
            width: 111,
            align: 'center',
            render: (shopManager: Store.ShopManagerManagement) =>
                shopManager.is_blocked ? 'Sim' : 'Não',
        },
        {
            title: 'Ativa',
            dataIndex: 'active',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (active: boolean) => (active ? 'Sim' : 'Não'),
        },
        {
            title: 'Aberta',
            dataIndex: 'is_open',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (isOpen: boolean) => (isOpen ? 'Sim' : 'Não'),
        },
        {
            title: 'TOTEM',
            dataIndex: 'totemCount',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (totemCount: number) => (totemCount ? 'Sim' : 'Não'),
        },
        {
            title: 'Trava?',
            dataIndex: 'lockCount',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (lockCount: number) => (lockCount > 0 ? 'Sim' : 'Não'),
        },
        {
            title: 'PWA',
            dataIndex: 'shopManager',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (shopManager: Store.ShopManagerManagement) =>
                shopManager?.active_pwa ? 'Sim' : 'Não',
        },
        {
            title: 'APP',
            dataIndex: 'shopManager',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (shopManager: Store.ShopManagerManagement) =>
                shopManager.active_app ? 'Sim' : 'Não',
        },
        {
            title: 'Cart App',
            dataIndex: 'paymentsStore',
            width: 111,
            align: 'center',
            ellipsis: true,
            render: (paymentsStore: Store.PaymentsStore[]) => {
                const paymentCreditCard = paymentsStore.filter(
                    (item) => item.paymentMethod.name === 'credit_card',
                );

                return paymentCreditCard[0]?.is_active ? 'Sim' : 'Não';
            },
        },
        {
            title: 'CFe',
            dataIndex: 'shopManager',
            width: 111,
            align: 'center',
            render: (shopManager: Store.ShopManager) =>
                shopManager.emit_invoice ? 'Sim' : 'Não',
            ellipsis: true,
        },
        {
            title: 'Grupo',
            dataIndex: 'groups',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (groups: StoreGroup.Group[]) =>
                groups?.map((g) => g.name).join(', ') || '-',
        },
        {
            title: 'Data da 1ª venda',
            dataIndex: 'first_sale',
            width: 148,
            align: 'center',
            sorter: true,
            ellipsis: true,
            render: (value: string) =>
                value ? dayjs(value).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Data da última venda',
            dataIndex: 'last_sale',
            width: 148,
            align: 'center',
            sorter: true,
            // ellipsis: true,
            render: (value) =>
                value ? dayjs(value).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Vendas m-1',
            dataIndex: 'sum_total_one_month_ago',
            width: 148,
            sorter: true,
            align: 'center',
            ellipsis: true,
            render: (price: number) => (
                <CellWithTooltip
                    valueCell={convertCurrency('', price)}
                    ptbrCurrency
                />
            ),
        },
        {
            title: 'Vendas m-2',
            dataIndex: 'sum_total_two_months_ago',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (price: number) => (
                <CellWithTooltip
                    valueCell={convertCurrency('', price)}
                    ptbrCurrency
                />
            ),
        },
        {
            title: 'Vendas m-3',
            dataIndex: 'sum_total_three_months_ago',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (price: number) => (
                <CellWithTooltip
                    valueCell={convertCurrency('', price)}
                    ptbrCurrency
                />
            ),
        },
        {
            title: 'Mensalidade InHouse aut. (m-1)',
            dataIndex: 'oneMonthAgoFinancialReport',
            width: 160,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.automatic_monthly_fee ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.automatic_monthly_fee,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse boleto (m-1)',
            dataIndex: 'oneMonthAgoFinancialReport',
            width: 172,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.monthly_invoice ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.monthly_invoice,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse aut. (m-2)',
            dataIndex: 'twoMonthAgoFinancialReport',
            width: 160,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.automatic_monthly_fee ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.automatic_monthly_fee,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse boleto (m-2)',
            dataIndex: 'twoMonthAgoFinancialReport',
            width: 172,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.monthly_invoice ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.monthly_invoice,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse aut. (m-3)',
            dataIndex: 'threeMonthAgoFinancialReport',
            width: 160,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.automatic_monthly_fee ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.automatic_monthly_fee,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse boleto (m-3)',
            dataIndex: 'threeMonthAgoFinancialReport',
            width: 172,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.monthly_invoice ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.monthly_invoice,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse aut. (m-4)',
            dataIndex: 'fourMonthAgoFinancialReport',
            width: 160,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.automatic_monthly_fee ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.automatic_monthly_fee,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse boleto (m-4)',
            dataIndex: 'fourMonthAgoFinancialReport',
            width: 172,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.monthly_invoice ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.monthly_invoice,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse aut. (m-5)',
            dataIndex: 'fiveMonthAgoFinancialReport',
            width: 160,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.automatic_monthly_fee ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.automatic_monthly_fee,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse boleto (m-5)',
            dataIndex: 'fiveMonthAgoFinancialReport',
            width: 172,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.monthly_invoice ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.monthly_invoice,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse aut. (m-6)',
            dataIndex: 'sixMonthAgoFinancialReport',
            width: 160,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.automatic_monthly_fee ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.automatic_monthly_fee,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Mensalidade InHouse boleto (m-6)',
            dataIndex: 'sixMonthAgoFinancialReport',
            width: 172,
            align: 'center',
            render: (invoice: Store.MonthAgoFinancialReport) => {
                return invoice?.monthly_invoice ? (
                    <CellWithTooltip
                        valueCell={convertCurrency(
                            '',
                            invoice?.monthly_invoice,
                        )}
                        ptbrCurrency
                    />
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Tipo de mensalidade',
            dataIndex: 'shopManager',
            width: 248,
            align: 'center',
            render: (shopManager: Store.ShopManagerManagement) => (
                <BadgeTypeBilling>
                    {BillingLabel[shopManager?.monthly_fee_type]}
                </BadgeTypeBilling>
            ),
        },
        {
            title: 'Valor da cobrança',
            dataIndex: 'shopManager',
            width: 148,
            align: 'center',
            ellipsis: true,
            render: (shopManager: Store.ShopManagerManagement) => (
                <CellWithTooltip
                    valueCell={convertCurrency(
                        '',
                        Number(shopManager?.monthly_fee_value) || 0,
                    )}
                    ptbrCurrency
                />
            ),
        },
        {
            title: '% PAGAR.ME',
            dataIndex: 'shopManager',
            width: 148,
            align: 'center',
            render: (shopManager: Store.ShopManagerManagement) =>
                shopManager?.percentage
                    ? `${shopManager?.percentage.replace('.', ',')}%`
                    : '-',
        },
        {
            title: '% PAGBANK',
            dataIndex: 'shopManager',
            width: 148,
            align: 'center',
            render: (shopManager: Store.ShopManagerManagement) =>
                shopManager?.pagbank_split_percentage
                    ? `${shopManager?.pagbank_split_percentage.replace(
                          '.',
                          ',',
                      )}%`
                    : '-',
        },
        {
            title: 'Regra válida até',
            dataIndex: 'validUntil',
            width: 148,
            align: 'center',
            render: (validUntil: string | null) =>
                validUntil ? dayjs(validUntil).format('DD/MM/YYYY') : '-',
        },
    ];

    return (
        <>
            <Table
                bordered
                pagination={false}
                loading={loading}
                className="table-to-hide"
                size="small"
                dataSource={dataSource || []}
                rowKey="id"
                rowSelection={rowSelection}
                columns={storesColumns}
                onChange={(
                    _,
                    __,
                    sorter: SorterResult<Store.StoreManagement>,
                ) => {
                    setFilters((oldData) => ({
                        ...oldData,
                        orderBy:
                            StoreListOrderBy[
                                sorter.field as keyof typeof StoreListOrderBy
                            ] ?? undefined,
                        orderDir: sorter.order
                            ? StoreListOrderDir[sorter.order]
                            : undefined,
                    }));
                }}
            />

            {/* modals */}
            <ConfirmModal
                visible={open}
                onCancel={() => {
                    if (assumeLoading) return;
                    cancelRecaptcha();
                    setOpen(false);
                }}
                onOk={() =>
                    handleAssume(
                        shoppManagerAssume.user_id, // user_id
                        shoppManagerAssume.shopmanager_id, // shopmanager_id
                        shoppManagerAssume.email, // email
                        shoppManagerAssume.isBlocked, // is_blocked
                        shoppManagerAssume.redirect, // redirect screen
                    )
                }
                confirmLoading={assumeLoading}
                okButtonProps={{ htmlType: 'submit', form: 'modal-form' }}
            >
                <ModalContainer id="modal-form">
                    <p style={{ textAlign: 'center' }}>
                        Você certeza que deseja assumir o papel de{' '}
                        {shoppManagerAssume.email}?
                    </p>
                </ModalContainer>
            </ConfirmModal>
            {open && <Recaptcha ref={recaptchaRef} />}
        </>
    );
};

export default TableStoreMAnagement;
